import {
  GRAMS_PER_KG,
  GRAMS_PER_LB,
  GRAMS_PER_OZ,
  OZ_PER_LB
} from "@common/helpers/WeightFormatter";

export type DecimalScaleVariant = "kg" | "g" | "lb" | "oz";
export type ScaleVariant = DecimalScaleVariant | "lbOz";

export const SCALE_UNIT_LABELS: Record<ScaleVariant, string> = {
  g: "g",
  kg: "kg",
  lb: "lb",
  lbOz: "lb oz",
  oz: "oz"
};

export type ParsedScaleInput =
  { grams: number } | { invalid: true; message?: string } | null;

const GRAMS_PER_UNIT: Record<DecimalScaleVariant, number> = {
  g: 1,
  kg: GRAMS_PER_KG,
  lb: GRAMS_PER_LB,
  oz: GRAMS_PER_OZ
};

export const NUMBERS_ONLY_ERROR = "Numbers only";

export const normalizeDecimal = (raw: string) => raw.trim().replace(",", ".");

export const isDecimalText = (normalized: string) => /^\d*\.?\d*$/.test(normalized);

const splitLbOz = (normalized: string) => {
  const [whole, fraction] = normalized.split(".");
  const digits = whole || "0";

  return {
    ounces:
      String(Number(digits.slice(-2))) + (fraction !== undefined ? `.${fraction}` : ""),
    pounds: digits.length > 2 ? Number(digits.slice(0, -2)) : null
  };
};

export const parseScaleInput = (raw: string, variant: ScaleVariant): ParsedScaleInput => {
  const normalized = normalizeDecimal(raw);
  if (normalized === "" || normalized === ".") return null;
  if (!isDecimalText(normalized)) return { invalid: true, message: NUMBERS_ONLY_ERROR };

  if (variant !== "lbOz") return { grams: Number(normalized) * GRAMS_PER_UNIT[variant] };

  const { ounces, pounds } = splitLbOz(normalized);
  const ounceCount = Number(ounces);
  if (pounds && ounceCount >= OZ_PER_LB) return { invalid: true };

  return { grams: (pounds ?? 0) * GRAMS_PER_LB + ounceCount * GRAMS_PER_OZ };
};

export const formatLbOzDigits = (raw: string): string => {
  const normalized = normalizeDecimal(raw);
  if (normalized === "" || !isDecimalText(normalized)) return raw;

  const { ounces, pounds } = splitLbOz(normalized);

  return pounds === null ? `${ounces} oz` : `${pounds} lb ${ounces} oz`;
};

const sanitizeLbOzDigits = (value: string) =>
  value
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1")
    .replace(/^0+(?=\d)/, "");

const commonPrefixLength = (a: string, b: string) => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
};

const commonSuffixLength = (a: string, b: string) => {
  let i = 0;
  while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) i++;
  return i;
};

export const nextLbOzDigits = (raw: string, shown: string, typed: string): string => {
  const prefix = commonPrefixLength(shown, typed);
  const suffix = commonSuffixLength(shown.slice(prefix), typed.slice(prefix));
  const inserted = typed.slice(prefix, typed.length - suffix);
  const removed = shown.length - prefix - suffix;

  if (removed === 0) return sanitizeLbOzDigits(raw + inserted);
  if (removed === shown.length) return sanitizeLbOzDigits(inserted);

  return sanitizeLbOzDigits(raw.slice(0, -1) + inserted);
};

const lbOzDigitsFromGrams = (grams: number) => {
  const totalOunces = grams / GRAMS_PER_OZ;
  let pounds = Math.floor(totalOunces / OZ_PER_LB + 1e-9);
  let ounces = Number((totalOunces - pounds * OZ_PER_LB).toFixed(1));
  if (ounces >= OZ_PER_LB) {
    pounds += 1;
    ounces = 0;
  }

  const [whole, fraction] = String(ounces).split(".");
  const digits = pounds > 0 ? `${pounds}${whole.padStart(2, "0")}` : whole;

  return fraction ? `${digits}.${fraction}` : digits;
};

export const scaleDigitsFromGrams = (
  grams: number | null,
  variant: ScaleVariant
): string => {
  if (grams == null || !(grams > 0)) return "";
  if (variant === "lbOz") return lbOzDigitsFromGrams(grams);

  return String(Math.round((grams / GRAMS_PER_UNIT[variant]) * 100) / 100);
};

export const formatScaleAmount = (grams: number, variant: ScaleVariant): string => {
  if (variant === "lbOz")
    return formatLbOzDigits(scaleDigitsFromGrams(grams, variant)) || "0 oz";

  return `${scaleDigitsFromGrams(grams, variant) || "0"} ${SCALE_UNIT_LABELS[variant]}`;
};

export const pricedUnit = (variant: ScaleVariant): DecimalScaleVariant =>
  variant === "lbOz" ? "lb" : variant;

export const gramsPerPricedUnit = (variant: ScaleVariant) =>
  GRAMS_PER_UNIT[pricedUnit(variant)];

const toCents = (price: number) => parseFloat(price.toFixed(2));

export const pricePerUnit = (valuePerGram: number, variant: ScaleVariant) =>
  toCents(valuePerGram * gramsPerPricedUnit(variant));

export const rescalePrice = (price: number, fromGrams: number, toGrams: number) =>
  toCents((price * toGrams) / fromGrams);

export const convertPrice = (price: number, from: ScaleVariant, to: ScaleVariant) =>
  rescalePrice(price, gramsPerPricedUnit(from), gramsPerPricedUnit(to));
